import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  Chip,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";
import { SvgIcon, type SvgIconProps } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type React from "react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import debounce from "lodash.debounce";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import DynamicTable from "../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../components/Modals/delete-modal";
import jsPDF from "jspdf";
import jsbarcode from "jsbarcode";
import { useLocation, useNavigate } from "react-router-dom";
import { useBloodBankDateQuery } from "../blood-bank-date";
import { useSelector } from "react-redux";
import DateRangeSelector from "../../../components/DateRangeSelector";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];


// Define allowed bag types
type BagType = "SINGLE" | "DOUBLE" | "TRIPLE" | "QUADRUPLE";

// Define component limit mapping
const componentLimits: Record<BagType, number> = {
  SINGLE: 1,
  DOUBLE: 2,
  TRIPLE: 3,
  QUADRUPLE: 4,
};

// Define data type for generatePDF
interface BagData {
  _id: string;
  bagType: BagType;
  bagNo: string;
  bloodGroup: string;
  rhFactor: string;
  collectionDate: string;
  expiryDate: string;
  donorData?: {
    name?: string;
    age?: string;
    gender?: string;
  };
  pageHeaderData?: {
    title?: string;
    address?: string;
  };
  bagComponent?: Array<{
    component: string;
    expiryDate: string;
    status: string;
  }>;
  bloodCell?: string[];
}
const TrashIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} viewBox="0 0 64 64">
      <circle cx="46" cy="6" r="4" fill="black" />
      <path d="M44 10 L26 16 L28 20 L44 14 Z" fill="black" />
      <path d="M44 10 L36 28 L44 28 L50 14 Z" fill="black" />
      <path d="M44 28 L46 50 L50 50 L48 28 Z" fill="black" />
      <path d="M36 28 L36 50 L40 50 L44 28 Z" fill="black" />
      <g stroke="black" strokeWidth="1" fill="none">
        <rect x="10" y="24" width="12" height="20" />
        <line x1="10" y1="24" x2="22" y2="44" />
        <line x1="22" y1="24" x2="10" y2="44" />
        <line x1="10" y1="30" x2="22" y2="30" />
        <line x1="10" y1="36" x2="22" y2="36" />
      </g>
      <polygon points="14,26 16,24 18,26" fill="black" />
      <polygon points="12,28 14,26 16,28" fill="black" />
    </SvgIcon>
  );
};

const ErrorText = ({ message }: { message: string }) => (
  <Typography sx={{ color: "red", fontSize: "11px" }}>{message}</Typography>
);

interface Donor {
  _id: string;
  donorId: string;
  name?: string;
  age?: string;
  gender?: string;
  cellNo?: string;
  bloodGroup?: string;
}

const formatDate = (dateString: string): string => {
  try {
    if (!dateString) return "N/A";
    const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateString).toLocaleDateString();
  } catch {
    return "N/A";
  }
};

const BagGeneration: React.FC = () => {
  const now = new Date();
  const localDateTime = new Date(
  now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const expiryDate = new Date(now);
  expiryDate.setMonth(now.getMonth() + 1);

  const [formData, setFormData] = useState({
    donorId: "",
    collectionDate: localDateTime,
    issueDate: "",
    expiryDate: localDateTime,
    bloodGroup: "",
    bagNo: "",
    rhFactor: "",
    bloodCell: [] as string[],
    bagType: "",
    bloodQuantity: "",
    du: "",
    bagStatus: "ACTIVE",
  });

  const [donorOptions, setDonorOptions] = useState<any[]>([]);
  const [donorLoading, setDonorLoading] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const {
    dateRange,
    setDateRange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    statusFilter,
    setStatusFilter,
  } = useBloodBankDateQuery(setPage, true);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [searchType, setSearchType] = useState("donorId");
  const [donorInputValue, setDonorInputValue] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [recentDonors, setRecentDonors] = useState<any[]>([]);
  const [selectedDonor, setSelectedDonor] = useState<any>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [pdfContent, setPdfContent] = useState<string>("");
  const [currentPrintBag, setCurrentPrintBag] = useState(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [bagType, setBagType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDonorSelected, setIsDonorSelected] = useState(false);
  const [isDisposeModalOpen, setIsDisposeModalOpen] = useState(false);
  const [disposeBagId, setDisposeBagId] = useState<string>("");
  const [disposalReason, setDisposalReason] = useState<string>("");

  const [selectedBloodProducts, setSelectedBloodProducts] = useState<{
    [bagId: string]: string;
  }>({});
  console.log("bagId", setSelectedBloodProducts, selectedBloodProducts);
  const location = useLocation();
  const { state } = location;
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );
  const hospitalName = projectProfile?.hospitalName || "N/A";
  const firstWord = hospitalName.split(" ")[0];

  const pathname = location.pathname;
  const navigate = useNavigate();

  const [errors, setErrors] = useState({
    donorId: "",
    collectionDate: "",
    expiryDate: "",
    bloodGroup: "",
    rhFactor: "",
    du: "",
    bloodCell: "",
    bagType: "",
    bloodQuantity: "",
  });

  const bloodProductOptions = [
    "Whole Blood",
    "Packed Red Cells",
    "Leucodepleted Packed Red Cells",
    "Washed Packed Red Cells",
    "Fresh Frozen Plasma",
    "Platelets",
    "Stored Plasma",
    "Frozen Stored Plasma",
    "Cryoprecipitate (CP)",
    "Cryosupernatant (CS)",
    "Buffcoat",
    "Mega Unit",
    "Plasma Exchange",
  ];

  const getMaxSelections = () => {
    switch (formData.bagType) {
      case "SINGLE":
        return 1;
      case "DOUBLE":
        return 2;
      case "TRIPLE":
        return 3;
      case "QUADRUPLE":
        return 4;
      default:
        return 0;
    }
  };

  const fetchRecentDonors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getDonors`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            pageNo: 1,
            count: 5,
          },
        }
      );
      setRecentDonors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching recent donors", error);
    }
  };

  const fetchDonorOptions = async (search: string) => {
    if (!search) return;
    try {
      setDonorLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getAllDonorsByFilter`,
        {
          params: { search, searchBy: searchType },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDonorOptions(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching donors", error);
      toast.error("Failed to fetch donors");
      setDonorOptions([]);
    } finally {
      setDonorLoading(false);
    }
  };

  const debouncedFetch = debounce(fetchDonorOptions, 400);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTableData = async () => {
    try {
      const params: any = {
        pageNo: page + 1,
        count: rowsPerPage,
        timestamp: new Date().getTime(), // Add cache-busting parameter
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (statusFilter && statusFilter.trim()) {
        params.status = statusFilter;
      }
      if (bloodGroup) {
        params.bloodGroup = bloodGroup;
      }
      if (bagType) {
        params.bagType = bagType;
      }
      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/findAllBags`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.data) {
        const populatedTableData = response.data.data.map((bag: any) => ({
          ...bag,
          donorData: bag.donorData || { name: "Unknown", donorId: "N/A" },
        }));
        setTableData(populatedTableData);
        setTotal(response.data?.totalCount || 0);
      } else {
        setTableData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Fetch Table Data Error:", error);
      setTableData([]);
      setTotal(0);
      toast.error("Fetch data have error");
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [page, rowsPerPage, statusFilter, debouncedSearch, bloodGroup, bagType, fromDate, toDate]);

  const resetForm = () => {
    const currentDate = new Date();
    const expiryDate = new Date(currentDate);
    expiryDate.setMonth(currentDate.getMonth() + 1);

    setFormData({
      donorId: "",
      collectionDate: localDateTime,
      issueDate: "",
      expiryDate: localDateTime,
      bloodGroup: "",
      bagNo: "",
      rhFactor: "",
      du: "",
      bloodCell: [],
      bagType: "",
      bloodQuantity: "",
      bagStatus: "ACTIVE",
    });
    setDonorInputValue("");
    setSearchType("donorId");
    setResetKey(prev => prev + 1);
    setEditData(null);
    setGenerateOpen(false);
    setSelectedDonor(null);
    setIsDonorSelected(false);
    setErrors({
      donorId: "",
      collectionDate: "",
      expiryDate: "",
      bloodGroup: "",
      rhFactor: "",
      du: "",
      bloodCell: "",
      bagType: "",
      bloodQuantity: "",
    });
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleBloodProductChange = (bagId: string, selectedProduct: string) => {
    setSelectedBloodProducts(prev => ({
      ...prev,
      [bagId]: selectedProduct,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const {
        donorId,
        collectionDate,
        expiryDate,
        issueDate,
        bloodGroup,
        bagNo,
        rhFactor,
        du,
        bloodCell,
        bagType,
        bloodQuantity,
      } = formData;

      setErrors({
        donorId: "",
        collectionDate: "",
        expiryDate: "",
        bloodGroup: "",
        rhFactor: "",
        du: "",
        bloodCell: "",
        bagType: "",
        bloodQuantity: "",
      });

      let hasErrors = false;
      const newErrors = { ...errors };

      if (!selectedDonor) {
        newErrors.donorId = "Please select a donor.";
        hasErrors = true;
      }

      if (!collectionDate) {
        newErrors.collectionDate = "Please provide collection date.";
        hasErrors = true;
      }

      const now = new Date();
      const collection = new Date(collectionDate);
      const expiry = new Date(expiryDate);
      const issue = issueDate ? new Date(issueDate) : null;

      if (collection > now) {
        toast.error("Collection date cannot be in the future.");
        setIsSubmitting(false);
        return;
      }

      if (!expiryDate) {
        newErrors.expiryDate = "Please provide expiry date.";
        hasErrors = true;
      }

      if (issue && (issue < collection || issue > expiry)) {
        toast.error("Issue date must be between collection and expiry dates.");
        setIsSubmitting(false);
        return;
      }

      if (!bloodGroup?.trim()) {
        newErrors.bloodGroup = "Please select a Blood Group.";
        hasErrors = true;
      }

      if (!rhFactor?.trim()) {
        newErrors.rhFactor = "Please select RH Factor.";
        hasErrors = true;
      }

      if (!bagType?.trim()) {
        newErrors.bagType = "Please select a Bag Type.";
        hasErrors = true;
      }
      // Validate exact number of blood products for each bag type
      if (bagType && componentLimits[bagType as BagType]) {
        const requiredCount = componentLimits[bagType as BagType];
        if (bloodCell.length !== requiredCount) {
          newErrors.bloodCell = `For a ${bagType} bag, exactly ${requiredCount} blood product(s) must be selected.`;
          toast.error(
            `For a ${bagType} bag, exactly ${requiredCount} blood product(s) must be selected.`
          );
          hasErrors = true;
        }
      }
      if (!bloodCell || bloodCell.length === 0) {
        newErrors.bloodCell = "Please select at least one Blood Cell.";
        hasErrors = true;
      }

      if (bloodCell.length > getMaxSelections()) {
        newErrors.bloodCell = `Maximum ${getMaxSelections()} blood products allowed for ${bagType} bag.`;
        hasErrors = true;
      }

      if (hasErrors) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      const currentBagData = editData
        ? tableData.find(bag => bag?._id === editData)
        : null;

      const donorData = selectedDonor
        ? {
            name: selectedDonor.name || "Unknown",
            donorId: selectedDonor.donorId || "N/A",
            age: selectedDonor.age || "N/A",
            gender: selectedDonor.gender || "N/A",
            cellNo: selectedDonor.cellNo || "N/A",
          }
        : currentBagData?.donorData || { name: "Unknown" };

      const bagComponent = bloodCell.map(component => ({
        component,
        expiryDate: expiryDate,
        status: "ACTIVE",
      }));

      // Convert local datetime to ISO format while preserving the local time
      const collectionISO = new Date(collection.getTime() - collection.getTimezoneOffset() * 60000).toISOString();
      const expiryISO = new Date(expiry.getTime() - expiry.getTimezoneOffset() * 60000).toISOString();
      const issueISO = issue ? new Date(issue.getTime() - issue.getTimezoneOffset() * 60000).toISOString() : null;

      const payload: any = {
        donorId,
        collectionDate: collectionISO,
        expiryDate: expiryISO,
        bloodGroup,
        bagNo,
        rhFactor,
        du,
        bagType,
        bloodQuantity,
        bloodCell,
        // bagComponent,
        bagStatus: formData.bagStatus,
        donorData,
        extra: {},
      };

      if (issueISO) {
        payload.issueDate = issueISO;
      }

      const url = editData
        ? `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/updateBagById/${editData}`
        : `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/createBag`;

      const method = editData ? axios.put : axios.post;

      const response = await method(url, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Update tableData with the response data for edit mode
      if (editData) {
        // Fetch the updated bag data directly to ensure consistency
        const updatedBagResponse = await axios.get(
          `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/getOneBagById/${editData}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const updatedBagData = updatedBagResponse.data?.data;

        setTableData(prevData =>
          prevData.map(bag =>
            bag?._id === editData
              ? {
                  ...bag,
                  ...updatedBagData, // Use the fresh data from getOneBagById
                  donorData: updatedBagData.donorData || donorData,
                  bloodCell: updatedBagData.bloodCell || bloodCell,
                  bagComponent: updatedBagData.bagComponent || bagComponent,
                }
              : bag
          )
        );

        // Reset selectedBloodProducts for the updated bag
        setSelectedBloodProducts(prev => ({
          ...prev,
          [editData]: "All",
        }));
      } else {
        await fetchTableData(); // For new bag, fetch all data
        const newBagData = response.data?.data;
        if (newBagData) {
          const bagDataWithDonor = {
            ...newBagData,
            donorData,
          };
          setCurrentPrintBag(bagDataWithDonor);
          generatePDF(bagDataWithDonor, false);
        }
      }

      toast.success(response.data?.message || "Bag saved successfully");
      resetForm();
    } catch (error: any) {
      console.error("Submit Error:", error);
      toast.error(
        error.response?.data?.message?.join(", ") ||
          error.message ||
          "Something went wrong"
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Replace the existing useEffect that handles edit mode with this fixed version:

  useEffect(() => {
    fetchRecentDonors();
    if (state && state.idd && state.data) {
      const { idd, data } = state;
      let bloodGroup = "";
      let rhFactor = "";
      if (data?.bloodGroup) {
        const match = data.bloodGroup.match(/^(A|B|AB|O)([+-])$/);
        if (match) {
          bloodGroup = match[1];
          rhFactor = match[2];
        }
      }

      const currentDate = new Date();
      const localCollectionDate = new Date(
        currentDate.getTime() - currentDate.getTimezoneOffset() * 60000
      ).toISOString().slice(0, 16);
      
      const expiryDate = new Date(currentDate);
      expiryDate.setMonth(currentDate.getMonth() + 1);
      const localExpiryDate = new Date(
        expiryDate.getTime() - expiryDate.getTimezoneOffset() * 60000
      ).toISOString().slice(0, 16);

      setSelectedDonor({
        _id: idd,
        name: data.name || "N/A",
        age: data.age || "N/A",
        gender: data.gender || "N/A",
        cellNo: data.cellNo || "N/A",
        donorId: data.donorId || "N/A",
        bloodGroup: data.bloodGroup || "N/A",
        collectionDate: data.collectionDate || localCollectionDate,
      });

      setFormData({
        donorId: idd,
        collectionDate: localCollectionDate,
        issueDate: data.issueDate || "",
        expiryDate: localExpiryDate,
        bloodGroup: bloodGroup || "",
        bagNo: data.bagNo || "",
        rhFactor: rhFactor || "",
        du: data.du || "",
        bloodCell: Array.isArray(data.bloodCell) ? data.bloodCell : [],
        bagType: data.bagType || "",
        bloodQuantity: data.bloodQuantity || "",
        bagStatus: data.bagStatus || "ACTIVE",
      });

      setIsDonorSelected(true);
      setDonorInputValue(`${data.donorId}-${data.name || "Unknown"}`);
      setGenerateOpen(true);
    } else if (state && state.editData && state.bagId) {
      const bag = state.editData;

      // Fixed: Parse blood group and Rh factor from combined bloodGroup field
      let bloodGroup = "";
      let rhFactor = "";

      // First try to parse from combined bloodGroup field (e.g., "A+", "B-")
      if (bag?.bloodGroup) {
        const combinedMatch = bag.bloodGroup.match(/^(A|B|AB|O)([+-])$/);
        if (combinedMatch) {
          bloodGroup = combinedMatch[1];
          rhFactor = combinedMatch[2];
        } else {
          // If it's just the blood group without Rh factor
          bloodGroup = bag.bloodGroup;
        }
      }

      // If rhFactor exists separately, use it
      if (bag?.rhFactor) {
        rhFactor = bag.rhFactor;
      }

      const bloodCell =
        bag.bagComponent && Array.isArray(bag.bagComponent)
          ? bag.bagComponent.map((comp: any) => comp.component)
          : Array.isArray(bag.bloodCell)
          ? bag.bloodCell
          : [];

      const bagType = ["SINGLE", "DOUBLE", "TRIPLE", "QUADRUPLE"].includes(
        bag.bagType
      )
        ? bag.bagType
        : "";

      setEditData(state.bagId);
      setSelectedDonor({
        _id: bag.donorId,
        name: bag.donorData?.name || "N/A",
        age: bag.donorData?.age || "N/A",
        gender: bag.donorData?.gender || "N/A",
        cellNo: bag.donorData?.cellNo || "N/A",
        donorId: bag.donorData?.donorId || "N/A",
        // Fixed: Set the combined blood group for donor display
        bloodGroup: `${bloodGroup}${rhFactor}` || "N/A",
        collectionDate: bag.collectionDate 
          ? bag.collectionDate.slice(0, 16)
          : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      });

      setFormData({
        donorId: bag.donorId || "",
        collectionDate: bag.collectionDate
          ? bag.collectionDate.slice(0, 16)
          : bag.collectionDate || "",
        issueDate: bag.issueDate || "",
        expiryDate: bag.expiryDate
          ? bag.expiryDate.slice(0, 16)
          : bag.expiryDate || "",
        // Fixed: Set individual blood group and Rh factor fields
        bloodGroup: bloodGroup || "",
        rhFactor: rhFactor || "",
        bagNo: bag.bagNo || "",
        du: bag.du || "",
        bloodCell: bloodCell,
        bagType: bagType,
        bloodQuantity: bag.bloodQuantity || "",
        bagStatus: bag.bagStatus || "ACTIVE",
      });

      setIsDonorSelected(true);
      setDonorInputValue(
        `${bag.donorData?.donorId || "N/A"}-${bag.donorData?.name || "Unknown"}`
      );

      setSelectedBloodProducts(prev => ({
        ...prev,
        [state.bagId]: "All",
      }));
      setGenerateOpen(true);
    }
  }, [state]);

  const handleBagTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBagType = e.target.value;
    setFormData(prev => ({
      ...prev,
      bagType: newBagType,
      bloodCell: [],
    }));
  };
  const handleEditClick = (id: string, data: any) => {
    navigate("/donor-bag", { state: { bagId: id, editData: data } });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItemId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/deleteBagById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Deleted successfully");
      fetchTableData();
    } catch (error: any) {
      toast.error(error.message || "Delete failed");
    }
    setIsDeleteModalOpen(false);
  };

  const generatePDF = (data: BagData, print = false) => {
    console.log("Generating PDF for data:", data);

    try {
      if (!data) {
        console.error("No data provided for PDF generation");
        toast.error("Failed to generate PDF - missing data");
        return;
      }

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [65, 90],
      });

      const margin = 3;
      const pageWidth = 65;

      const bagId = data._id;
      const selectedProduct = selectedBloodProducts[bagId] || "All";
      const componentsToPrint =
        selectedProduct === "All" &&
        data.bagComponent &&
        Array.isArray(data.bagComponent) &&
        data.bagComponent.length > 0
          ? data.bagComponent.slice(0, componentLimits[data.bagType] || 1)
          : selectedProduct === "All" && data.bloodCell
          ? Array.isArray(data.bloodCell)
            ? data.bloodCell
                .map((component: string, compIdx: number) => ({
                  component,
                  expiryDate: data.expiryDate,
                  _id: `fallback-${bagId}-${compIdx}`,
                }))
                .slice(0, componentLimits[data.bagType] || 1)
            : [
                {
                  component: data.bloodCell,
                  expiryDate: data.expiryDate,
                  _id: `fallback-${bagId}`,
                },
              ]
          : data.bagComponent &&
            Array.isArray(data.bagComponent) &&
            data.bagComponent.length > 0
          ? data.bagComponent.filter(comp => comp.component === selectedProduct)
          : data.bloodCell
          ? Array.isArray(data.bloodCell)
            ? data.bloodCell
                .filter((component: string) => component === selectedProduct)
                .map((component: string, compIdx: number) => ({
                  component,
                  expiryDate: data.expiryDate,
                  _id: `fallback-${bagId}-${compIdx}`,
                }))
            : data.bloodCell === selectedProduct
            ? [
                {
                  component: data.bloodCell,
                  expiryDate: data.expiryDate,
                  _id: `fallback-${bagId}`,
                },
              ]
            : []
          : [
              {
                component: "WHOLE BLOOD",
                expiryDate: data.expiryDate,
                _id: `fallback-${bagId}`,
              },
            ];

      componentsToPrint.forEach((component: any, index: number) => {
        if (index > 0) {
          doc.addPage();
        }

        let yPos = 5;

        doc.setFont("times", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0);
        const headerText =
          data.pageHeaderData?.title && data.pageHeaderData.title.trim() !== ""
            ? data.pageHeaderData.title.toUpperCase()
            : projectProfile?.hospitalName?.toUpperCase() || "N/A";

        const headerTextWidth = doc.getTextWidth(headerText);
        const headerX = (pageWidth - headerTextWidth) / 2;
        doc.text(headerText, headerX, yPos);

        yPos += 4;
        doc.setFont("times", "normal");
        doc.setFontSize(8);
        const subheaderText =
          data.pageHeaderData?.address &&
          data.pageHeaderData.address.trim() !== ""
            ? data.pageHeaderData.address
            : projectProfile?.address || "";

        const subheaderTextWidth = doc.getTextWidth(subheaderText);
        const subheaderX = (pageWidth - subheaderTextWidth) / 2;
        doc.text(subheaderText, subheaderX, yPos);

        yPos += 5;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);

        const bloodComponentText =
          component.component?.toUpperCase() || "WHOLE BLOOD";
        const maxWidth = pageWidth - margin * 2;
        const bloodComponentLines = doc.splitTextToSize(
          bloodComponentText,
          maxWidth
        );
        const firstLineWidth = doc.getTextWidth(bloodComponentLines[0]);
        const bloodComponentX = (pageWidth - firstLineWidth) / 2;
        doc.text(bloodComponentLines[0], bloodComponentX, yPos);

        if (bloodComponentLines.length > 1) {
          for (let i = 1; i < bloodComponentLines.length; i++) {
            yPos += 3;
            const lineWidth = doc.getTextWidth(bloodComponentLines[i]);
            const lineX = (pageWidth - lineWidth) / 2;
            doc.text(bloodComponentLines[i], lineX, yPos);
          }
        }

        doc.setDrawColor(0);
        doc.line(margin, yPos + 1, pageWidth - margin, yPos + 1);

        yPos += 5;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        const donorName = data.donorData?.name
          ? `${data.donorData.name.toUpperCase()}`
          : "UNKNOWN";
        const donorNameX = margin + 1;
        doc.text(donorName, donorNameX, yPos);

        yPos += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);

        const ageText = `Age: ${data.donorData?.age || "N/A"}`;
        const genderText = `Gender: ${data.donorData?.gender || "N/A"}`;
        doc.text(ageText, margin, yPos);
        doc.text(genderText, margin + 20, yPos);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.text(
          `${data.bloodGroup || "N/A"}`,
          pageWidth - margin - 9,
          yPos - 2
        );

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.text(
          `${data.rhFactor === "+" ? "POSITIVE" : "NEGATIVE"}`,
          pageWidth - margin - 10,
          yPos + 2
        );

        yPos += 5;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text(`Bag No: ${data.bagNo || "N/A"}`, margin, yPos);

        yPos += 5;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text(`Collection Date`, margin, yPos);
        doc.text(
          `${formatDate(data.collectionDate) || "N/A"}`,
          margin + 20,
          yPos
        );

        yPos += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text(`Expiry Date`, margin, yPos);
        doc.text(
          `${formatDate(component.expiryDate || data.expiryDate) || "N/A"}`,
          margin + 15,
          yPos
        );

        yPos += 6;
        try {
          const barcodeCanvas = document.createElement("canvas");
          barcodeCanvas.width = 800;
          barcodeCanvas.height = 100;
          const ctx = barcodeCanvas.getContext("2d");
          if (ctx) {
            ctx.imageSmoothingEnabled = false;
          }

          const barcodeValue = data.bagNo || "UNKNOWN";
          jsbarcode(barcodeCanvas, barcodeValue, {
            format: "CODE128",
            width: 1,
            height: 15,
            fontSize: 7,
            displayValue: false,
            margin: 0,
          });

          const barcodeDataUrl = barcodeCanvas.toDataURL("image/png", 1.0);
          const barcodeWidth = 40;
          const barcodeX = (pageWidth - barcodeWidth) / 2;
          doc.addImage(barcodeDataUrl, "PNG", barcodeX, yPos, barcodeWidth, 10);
          yPos += 10;
        } catch (barcodeError) {
          console.error("Barcode generation failed:", barcodeError);
          yPos += 5;
          doc.setFontSize(7);
          doc.setTextColor(0);
          doc.setFont("helvetica", "normal");
          const bagNoText = `Bag No: ${data.bagNo || "UNKNOWN"}`;
          const bagNoTextWidth = doc.getTextWidth(bagNoText);
          const bagNoX = (pageWidth - bagNoTextWidth) / 2;
          doc.text(bagNoText, bagNoX, yPos);
          yPos += 5;
        }

        yPos += 15;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(5);
        const footerText =
          `This blood product has been screened for HBsAg, HCV, HIV, MP & VDRL. ${component.storageMessage || ''}`;
        const footerLines = doc.splitTextToSize(
          footerText,
          pageWidth - margin * 2
        );
        doc.text(footerLines, margin, yPos);
      });

      if (print) {
        window.open(doc.output("bloburl"), "_blank");
      } else {
        const blob = doc.output("blob");
        const url = URL.createObjectURL(blob);
        
        // Revoke old URL if it exists
        if (pdfContent && pdfContent.startsWith("blob:")) {
          URL.revokeObjectURL(pdfContent);
        }
        
        setPdfContent(url);
        //@ts-ignore
        setCurrentPrintBag(data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error("PDF Generation Error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handlePrintClick = (index: any) => {
    const selectedBag = tableData[index];
    if (selectedBag) {
      if (!selectedBag.donorData || !selectedBag.donorData.name) {
        console.warn("Donor data missing or incomplete, using available data");
      }
      setCurrentPrintBag(selectedBag);
      generatePDF(selectedBag, false);
    } else {
      toast.error("Could not find the selected bag");
    }
  };

  const handlePrintFromPreview = () => {
    if (currentPrintBag) {
      generatePDF(currentPrintBag, true);
    } else {
      toast.error("No bag data available to print");
    }
  };

  const handleClosePreviewModal = () => {
    // Revoke Blob URL when closing modal to free memory
    if (pdfContent && pdfContent.startsWith("blob:")) {
      URL.revokeObjectURL(pdfContent);
    }
    setPdfContent(""); // Clear the content

    if (state) {
      navigate("/patient-slip");
    }
    setPreviewModalOpen(false);
  };

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "#FFFFFF",
              borderRadius: 0.5,
              padding: 0.5,
            }}
          >
            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", fontSize: "18px", mb: 1 }}
              >
                {pathname == "/blood-bags" ? "Blood Bags" : "Bag Generation"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end", width: "100%" }}>
                <TextField
                  className="custom-input"
                  size="small"
                  placeholder="Donor ID or name"
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      setPage(0);
                      fetchTableData();
                    }
                  }}
                  sx={{ width: 200 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "action.active" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          onClick={() => {
                            setSearchTerm("");
                            setPage(0);
                          }}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <TextField
                  className="custom-input"
                  select
                  label="Filter by Status"
                  value={statusFilter}
                  onChange={e => handleStatusFilterChange(e.target.value)}
                  size="small"
                  sx={{
                    width: 200,
                    backgroundColor: "#F5F5FB",
                    borderRadius: "8px",
                  }}
                  InputProps={{
                    endAdornment: statusFilter ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear filter"
                          onClick={() => handleStatusFilterChange("")}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="ISSUED">Issued</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                  <MenuItem value="DISPOSED">Disposed</MenuItem>
                </TextField>
                <TextField
                  className="custom-input"
                  select
                  label="Blood Group"
                  value={bloodGroup}
                  onChange={e => {
                    setBloodGroup(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  sx={{ width: 130, backgroundColor: "#F5F5FB", borderRadius: "8px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {BLOOD_GROUPS.map(group => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  className="custom-input"
                  select
                  label="Bag Type"
                  value={bagType}
                  onChange={e => {
                    setBagType(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  sx={{ width: 140, backgroundColor: "#F5F5FB", borderRadius: "8px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="SINGLE">Single</MenuItem>
                  <MenuItem value="DOUBLE">Double</MenuItem>
                  <MenuItem value="TRIPLE">Triple</MenuItem>
                  <MenuItem value="QUADRUPLE">Quadruple</MenuItem>
                </TextField>
                <DateRangeSelector
                  preset="blood"
                  compact
                  dateRange={dateRange}
                  fromDate={fromDate}
                  toDate={toDate}
                  onDateRangeChange={value => {
                    setDateRange(value);
                    setPage(0);
                  }}
                  onFromDateChange={value => {
                    setFromDate(value);
                    setPage(0);
                  }}
                  onToDateChange={value => {
                    setToDate(value);
                    setPage(0);
                  }}
                />
                {pathname !== "/blood-bags" && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      resetForm();
                      setGenerateOpen(true);
                    }}
                    sx={{ ml: "auto", height: 40, whiteSpace: "nowrap" }}
                  >
                    Generate Bag
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Dialog
          open={generateOpen}
          onClose={() => {
            setGenerateOpen(false);
            resetForm();
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
            }}
          >
            {editData ? "Update Bag" : "Generate Bag"}
            <IconButton
              aria-label="close"
              onClick={() => resetForm()}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2} sx={{ alignItems: "center", mb: 2 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ position: "relative", flexGrow: 1 }}>
                      <Autocomplete
                        key={resetKey}
                        options={donorOptions}
                        getOptionLabel={option =>
                          `${option.donorId}-${option.name || "Unknown"}`
                        }
                        inputValue={donorInputValue}
                        onInputChange={(e, newInputValue) => {
                          setDonorInputValue(newInputValue);
                          setIsDonorSelected(false);
                        }}
                        onChange={(e, value) => {
                          if (value) {
                            let bloodGroup = "";
                            let rhFactor = "";
                            if (value?.bloodGroup) {
                              const match =
                                value.bloodGroup.match(/^(A|B|AB|O)([+-])$/);
                              if (match) {
                                bloodGroup = match[1];
                                rhFactor = match[2];
                              }
                            }
                            const currentDate = new Date();
                            const localCollectionDate = new Date(
                              currentDate.getTime() - currentDate.getTimezoneOffset() * 60000
                            ).toISOString().slice(0, 16);
                            
                            const expiryDate = new Date(currentDate);
                            expiryDate.setMonth(currentDate.getMonth() + 1);
                            const localExpiryDate = new Date(
                              expiryDate.getTime() - expiryDate.getTimezoneOffset() * 60000
                            ).toISOString().slice(0, 16);

                            setFormData(prev => ({
                              ...prev,
                              donorId: value?._id,
                              bloodGroup: bloodGroup || "",
                              rhFactor: rhFactor || "",
                              collectionDate: localCollectionDate,
                              expiryDate: localExpiryDate,
                            }));
                            setSelectedDonor(value);
                            setIsDonorSelected(true);
                          } else {
                            const currentDate = new Date();
                            const localCollectionDate = new Date(
                              currentDate.getTime() - currentDate.getTimezoneOffset() * 60000
                            ).toISOString().slice(0, 16);
                            
                            const expiryDate = new Date(currentDate);
                            expiryDate.setMonth(currentDate.getMonth() + 1);
                            const localExpiryDate = new Date(
                              expiryDate.getTime() - expiryDate.getTimezoneOffset() * 60000
                            ).toISOString().slice(0, 16);

                            setFormData(prev => ({
                              ...prev,
                              donorId: "",
                              collectionDate: localCollectionDate,
                              expiryDate: localExpiryDate,
                              bloodCell: [],
                            }));
                            setSelectedDonor(null);
                            setIsDonorSelected(false);
                            setDonorOptions([]);
                          }
                        }}
                        onOpen={() => {
                          if (!donorInputValue) {
                            setDonorOptions(recentDonors);
                          }
                        }}
                        loading={donorLoading}
                        renderInput={params => (
                          <TextField
                            className="custom-input"
                            {...params}
                            label="Search by Donor ID"
                            size="small"
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                if (isDonorSelected) {
                                  return;
                                }
                                if (donorInputValue) {
                                  debouncedFetch.cancel();
                                  fetchDonorOptions(donorInputValue);
                                }
                              }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              style: { fontSize: "12px" },
                              endAdornment: (
                                <>
                                  {donorLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => {
                                          if (
                                            donorInputValue &&
                                            !isDonorSelected
                                          ) {
                                            debouncedFetch.cancel();
                                            fetchDonorOptions(donorInputValue);
                                          }
                                        }}
                                        edge="end"
                                        size="small"
                                        disabled={isDonorSelected}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        ListboxProps={{
                          style: {
                            maxHeight: "200px",
                            overflow: "auto",
                          },
                        }}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "sameWidth",
                                enabled: true,
                                phase: "beforeWrite",
                                requires: ["computeStyles"],
                                fn: ({ state }) => {
                                  state.styles.popper.width = `${state.rects.reference.width}px`;
                                },
                              },
                            ],
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      backgroundColor: "#f9f9f9",
                      mb: 1,
                    }}
                  >
                    <Grid container spacing={0}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Name:</strong> {selectedDonor?.name || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Age:</strong> {selectedDonor?.age || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Gender:</strong>{" "}
                          {selectedDonor?.gender || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Cell No:</strong>{" "}
                          {selectedDonor?.cellNo || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Donor ID:</strong>{" "}
                          {selectedDonor?.donorId || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: "11px" }}>
                          <strong>Blood Group:</strong>{" "}
                          {selectedDonor?.bloodGroup || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {errors.donorId && <ErrorText message={errors.donorId} />}

              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    label="Collection Date"
                    name="collectionDate"
                    type="datetime-local"
                    value={formData.collectionDate.slice(0, 16)}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.collectionDate
                            ? "red"
                            : "inherit",
                        },
                      },
                      opacity: isDonorSelected ? 1 : 0.7,
                    }}
                    error={!!errors.collectionDate}
                  />
                  {errors.collectionDate && (
                    <ErrorText message={errors.collectionDate} />
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    label="Expiry Date"
                    name="expiryDate"
                    type="datetime-local"
                    value={formData.expiryDate.slice(0, 16)}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.expiryDate ? "red" : "inherit",
                        },
                      },
                    }}
                    error={!!errors.expiryDate}
                  />
                  {errors.expiryDate && (
                    <ErrorText message={errors.expiryDate} />
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    select
                    label="Blood Group"
                    required
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.bloodGroup ? "red" : "inherit",
                        },
                      },
                      "& .MuiInputLabel-shrink": {
                        ...(formData.bloodGroup ? { display: "none" } : {}),
                      },
                    }}
                    error={!!errors.bloodGroup}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    {["A", "B", "AB", "O"].map(group => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.bloodGroup && (
                    <ErrorText message={errors.bloodGroup} />
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    select
                    label="RH Factor"
                    name="rhFactor"
                    required
                    value={formData.rhFactor}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.rhFactor ? "red" : "inherit",
                        },
                      },
                      "& .MuiInputLabel-shrink": {
                        ...(formData.rhFactor ? { display: "none" } : {}),
                      },
                    }}
                    error={!!errors.rhFactor}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    {["+", "-"].map(factor => (
                      <MenuItem key={factor} value={factor}>
                        {factor}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.rhFactor && <ErrorText message={errors.rhFactor} />}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    select
                    label="DU"
                    name="du"
                    value={formData.du}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.du ? "red" : "inherit",
                        },
                      },
                    }}
                    error={!!errors.du}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="DU+">DU+</MenuItem>
                    <MenuItem value="DU-">DU-</MenuItem>
                  </TextField>
                  {errors.du && <ErrorText message={errors.du} />}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    select
                    label="Bag Type"
                    required
                    name="bagType"
                    value={formData.bagType || ""}
                    onChange={handleBagTypeChange}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                    }}
                  >
                    <MenuItem value="SINGLE">Single</MenuItem>
                    <MenuItem value="DOUBLE">Double</MenuItem>
                    <MenuItem value="TRIPLE">Triple</MenuItem>
                    <MenuItem value="QUADRUPLE">Quadruple</MenuItem>
                  </TextField>
                  <ErrorText message={errors.bagType} />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={bloodProductOptions}
                    value={formData.bloodCell}
                    onChange={(e, value) => {
                      if (value.length <= getMaxSelections()) {
                        setFormData(prev => ({
                          ...prev,
                          bloodCell: value,
                        }));
                      } else {
                        toast.error(
                          `Maximum ${getMaxSelections()} blood products allowed for ${
                            formData.bagType
                          } bag`
                        );
                      }
                    }}
                    // Disable options when the maximum selection limit is reached
                    getOptionDisabled={option =>
                      formData.bloodCell.length >= getMaxSelections() &&
                      !formData.bloodCell.includes(option)
                    }
                    size="small"
                    renderInput={params => (
                      <TextField
                        className="custom-input"
                        required
                        {...params}
                        label="Blood Product"
                        size="small"
                        sx={{
                          fontSize: "8px",
                          backgroundColor: "#F5F5FB",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: errors.bloodCell ? "red" : "inherit",
                            },
                            minHeight:
                              formData.bagType === "QUADRUPLE"
                                ? "60px"
                                : "40px",
                            padding: "4px 8px",
                            flexWrap: "wrap",
                          },
                        }}
                        error={!!errors.bloodCell}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected} // auto tick when selected
                        />
                        {option}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          // ðŸ‘‡ only first word show hoga
                          label={option.split(" ")[0]}
                          {...getTagProps({ index })}
                          sx={{
                            fontSize: "8px",
                            height: "16px",
                            padding: "0px 2px",
                            margin: "1px",
                            borderRadius: "2px",
                            maxWidth: "60px", // small width so 4 chips fit in line
                            "& .MuiChip-label": {
                              padding: "0 4px",
                            },
                          }}
                        />
                      ))
                    }
                    slotProps={{
                      popper: {
                        style: { width: "300px" },
                      },
                    }}
                  />
                  {errors.bloodCell && <ErrorText message={errors.bloodCell} />}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    select
                    label="Status"
                    required
                    name="bagStatus"
                    value={formData.bagStatus}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="ISSUED">Issued</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className="custom-input"
                    label="Blood Quantity "
                    name="bloodQuantity"
                    value={formData.bloodQuantity}
                    onChange={e => {
                      const value = e.target.value;
                      if (/^\d{0,3}$/.test(value)) {
                        setFormData({ ...formData, bloodQuantity: value });
                      }
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "#F5F5FB",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.bloodQuantity ? "red" : "inherit",
                        },
                      },
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 3,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ fontSize: "10px" }}
                        >
                          ml
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {editData && (
                  <Grid item xs={6}>
                    <TextField
                      className="custom-input"
                      label="Bag Number"
                      value={
                        editData
                          ? tableData.find(bag => bag._id === editData)
                              ?.bagNo || "N/A"
                          : ""
                      }
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "#F5F5FB",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                    gap: 2,
                  }}
                >
                  {editData && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        height: "36px",
                        fontSize: "12px",
                        borderRadius: "6px",
                      }}
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Exit Edit Mode
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => resetForm()}
                    sx={{
                      height: "36px",
                      fontSize: "12px",
                      borderRadius: "6px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      height: "36px",
                      fontSize: "12px",
                      borderRadius: "6px",
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : editData ? (
                      "Update Bag"
                    ) : (
                      "Save Bag"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
        </Dialog>

        <Grid item xs={12}>
          <Box
            sx={{
              background: "#FFFFFF",
              borderRadius: 2,
              p: 2,
              boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
              minHeight: "485px",
              height: "auto",
            }}
          >
            <DynamicTable
              headers={[
                "SN",
                "Donor Name",
                "ID",
                "Bag No",
                "BG",
                "Bag Type",
                "Blood Quantity",
                "Status",
                "D.T.Expire",
                `${pathname == "/donor-bag" ? "" : "Expired At"}`,
                "Blood Product",
                "Actions",
              ]}
              data={tableData?.map((item, idx) => {
                console.log("Table row data:", item);
                console.log("Table row bloodCell:", item.bloodCell);
                console.log("Table row bagComponent:", item.bagComponent);

                // Find the status for the selected blood product
                const selectedProduct =
                  selectedBloodProducts[item._id] || "All";
                const selectedComponent =
                  item.bagComponent && Array.isArray(item.bagComponent)
                    ? item.bagComponent.find(
                        (comp: any) => comp.component === selectedProduct
                      )
                    : null;
                const componentStatus = selectedComponent
                  ? selectedComponent.status
                  : null;

                return [
                  page * rowsPerPage + idx + 1,
                  item.donorData?.name || "N/A",
                  item.donorData?.donorId || "N/A",
                  item.bagNo || "N/A",
                  item.bloodGroup + item.rhFactor || "-",
                  item.bagType || "N/A",
                  item.bloodQuantity ? `${item.bloodQuantity} ml` : "-",
                  item.bagStatus,
                  typeof item.daysToExpire !== "undefined"
                    ? `${item.daysToExpire} days`
                    : "N/A",
                  `${pathname == "/donor-bag" ? "" : item.expiredAt || "N/A"}`,
                  <Box sx={{ position: "relative", minWidth: 120 }}>
                    <TextField
                      className="custom-input"
                      select
                      size="small"
                      value={selectedBloodProducts[item._id] || "All"}
                      onChange={e => {
                        setSelectedBloodProducts(prev => ({
                          ...prev,
                          [item._id]: e.target.value,
                        }));
                      }}
                      sx={{ width: "100%" }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {(item.bagComponent && Array.isArray(item.bagComponent)
                        ? item.bagComponent.map(
                            (comp: any, compIdx: number) => ({
                              _id: comp._id || `fallback-${idx}-${compIdx}`,
                              component: comp.component,
                              status: comp.status || "UNKNOWN",
                            })
                          )
                        : Array.isArray(item.bloodCell)
                        ? item.bloodCell.map(
                            (component: string, compIdx: number) => ({
                              _id: `fallback-${idx}-${compIdx}`,
                              component,
                              status: "ACTIVE",
                            })
                          )
                        : [
                            {
                              _id: `fallback-${idx}`,
                              component: "WHOLE BLOOD",
                              status: "ACTIVE",
                            },
                          ]
                      ).map((comp: any) => (
                        <MenuItem
                          key={comp._id}
                          value={comp.component}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: "10px",
                          }}
                        >
                          {comp.component}
                          <Chip
                            label={comp.status}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: "16px",
                              backgroundColor:
                                comp.status === "ACTIVE"
                                  ? "#e0f7fa"
                                  : "#ffebee",
                              color:
                                comp.status === "ACTIVE"
                                  ? "#00796b"
                                  : "#c62828",
                              padding: "0 4px",
                              "& .MuiChip-label": {
                                padding: "0 6px",
                              },
                            }}
                          />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>,
                ];
              })}
              loading={false}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRows={total}
              handleChangePage={(e: any, newPage: any) => setPage(newPage)}
              handleChangeRowsPerPage={(e: any) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              height={"63vh"}
              actionButtons={[
                {
                  icon: (
                    <EditIcon
                      sx={{
                        color: "#FFB800",
                        cursor: "pointer",
                        width: "18px",
                      }}
                    />
                  ),
                  onClick: (index: any) =>
                    handleEditClick(tableData[index]?._id, tableData[index]),
                },
                {
                  icon: (
                    <PrintIcon
                      sx={{
                        color: "blue",
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    />
                  ),
                  onClick: (index: any) => handlePrintClick(index),
                },
                {
                  icon: (
                    <IconButton
                      color="error"
                      size="small"
                      title="Dispose"
                      sx={{
                        color: "#FF5454",
                        cursor: "pointer",
                        width: "18px",
                      }}
                    >
                      <TrashIcon />
                    </IconButton>
                  ),
                  onClick: (index: any) => {
                    setDisposeBagId(tableData[index]?._id);
                    setIsDisposeModalOpen(true);
                  },
                },
              ]}
            />
          </Box>
        </Grid>
      </Grid>

      <Modal
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "5px",
            width: "300px",
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            PDF Previe
          </Typography>
          <Box sx={{ border: "1px solid #ccc", borderRadius: "4px", overflow: "hidden" }}>
            <iframe 
              src={pdfContent} 
              width="100%" 
              height="400px" 
              title="PDF Preview"
              style={{ border: "none" }}
            />
          </Box>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              onClick={handlePrintFromPreview}
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
            >
              Print
            </Button>
            <Button
              onClick={handleClosePreviewModal}
              variant="contained"
              color="secondary"
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />

      <Modal
        open={isDisposeModalOpen}
        onClose={() => setIsDisposeModalOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "5px",
            width: "350px",
          }}
        >
          <Typography variant="h6" mb={2}>
            Dispose Blood Bag
          </Typography>
          <TextField
            className="custom-input"
            label="Disposal Reason"
            value={disposalReason}
            onChange={e => setDisposalReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                if (!disposalReason.trim()) {
                  toast.error("Please enter a disposal reason");
                  return;
                }
                try {
                  await axios.put(
                    `${process.env.REACT_APP_BLOOD_BANK}/bag-generation/disposedBagById/${disposeBagId}`,
                    { disposalReason },
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                          "token"
                        )}`,
                      },
                    }
                  );
                  toast.success("Bag disposed successfully");
                  setIsDisposeModalOpen(false);
                  setDisposalReason("");
                  setTableData(prev =>
                    prev.map(bag =>
                      bag._id === disposeBagId
                        ? { ...bag, bagStatus: "DISPOSED" }
                        : bag
                    )
                  );
                } catch (error: any) {
                  toast.error(
                    error.response?.data?.message ||
                      error.message ||
                      "Dispose failed"
                  );
                }
              }}
            >
              Confirm Dispose
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsDisposeModalOpen(false)}
            >
              Cancel
            </Button> 
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default BagGeneration;
